import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PROJECTS } from "../data";
import { useReveal } from "../hooks/useReveal";
import { Dialog, DialogContent } from "./ui/dialog";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = ["Tutti", "Residenziale", "Terrazzi", "Balconi", "Hospitality"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const Portfolio = () => {
  const [filter, setFilter] = useState("Tutti");
  const [activeIdx, setActiveIdx] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [ref, inView] = useReveal();

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/portfolio`)
      .then(({ data }) =>
        setUploaded(
          data.map((d) => ({ url: `${BACKEND}${d.image_url}`, category: d.category, title: d.title, span: false }))
        )
      )
      .catch(() => {});
  }, []);

  const all = [...uploaded, ...PROJECTS];
  const base = filter === "Tutti" ? all : all.filter((p) => p.category === filter);
  // Raggruppa le foto dello stesso progetto (stessa categoria + stesso titolo) una accanto all'altra
  const groupOrder = [];
  const groupMap = {};
  base.forEach((p) => {
    const key = `${p.category}__${(p.title || "").trim().toLowerCase()}`;
    if (!groupMap[key]) {
      groupMap[key] = [];
      groupOrder.push(key);
    }
    groupMap[key].push(p);
  });
  const visible = groupOrder.flatMap((k) => groupMap[k]);
  const isOpen = activeIdx !== null;
  const active = isOpen ? visible[activeIdx] : null;

  const prev = useCallback(() => setActiveIdx((i) => (i - 1 + visible.length) % visible.length), [visible.length]);
  const next = useCallback(() => setActiveIdx((i) => (i + 1) % visible.length), [visible.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto">
        <div ref={ref} className={`mb-10 text-center reveal ${inView ? "in-view" : ""}`}>
          <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-5">Realizzazioni</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight max-w-3xl mx-auto">
            Progetti reali, spazi trasformati.
          </h2>
          <div className="max-w-3xl mx-auto space-y-4 mt-6 text-foreground/70 font-light leading-relaxed">
            <p>Ogni installazione racconta una storia: quella di uno spazio che cambia, acquista nuova luce e trova una nuova dimensione di comfort e design.</p>
            <p>Nel nostro portfolio raccogliamo alcune delle realizzazioni VETRO DESIGN ITALIA: vetrate panoramiche VEPA progettate e installate in contesti residenziali e professionali, dove tecnologia, estetica e funzionalità si incontrano. Ogni progetto nasce da un'attenta analisi degli ambienti e viene sviluppato su misura, rispettando lo stile architettonico esistente e valorizzando al massimo la vista, la luminosità e la vivibilità degli spazi.</p>
            <p>Dai terrazzi panoramici ai balconi, dai dehor alle soluzioni indoor &amp; outdoor, ogni immagine rappresenta il risultato di un lavoro fatto di precisione, esperienza e cura dei dettagli. Lasciati ispirare dalle nostre realizzazioni e scopri come una vetrata panoramica può trasformare anche il tuo spazio in un ambiente unico da vivere tutto l'anno.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-12 justify-center">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`text-xs uppercase tracking-wide px-5 py-2.5 border transition-colors duration-300 ${
                filter === c ? "bg-black text-white border-black" : "border-black/20 hover:border-black"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 auto-rows-[260px] md:auto-rows-[320px]">
          {visible.map((p, i) => (
            <button
              key={i}
              data-testid={`portfolio-item-${i}`}
              onClick={() => setActiveIdx(i)}
              className={`group relative overflow-hidden border border-black/10 ${p.span ? "row-span-2" : ""}`}
            >
              <img
                src={p.url}
                alt={`${p.title} — vetrata panoramica VEPA ${p.category} | VETRO DESIGN ITALIA`}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 p-5 text-left opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/80">{p.category}</p>
                <p className="font-serif text-xl text-white">{p.title}</p>
              </div>
              <span className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/80 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Plus strokeWidth={1.2} className="w-4 h-4" />
              </span>
            </button>
          ))}
        </div>

        {visible.length === 0 && (
          <p data-testid="portfolio-empty" className="text-center text-foreground/50 font-light py-16">
            Nuove realizzazioni in arrivo. Torna presto a trovarci.
          </p>
        )}
      </div>

      <Dialog open={isOpen} onOpenChange={(o) => !o && setActiveIdx(null)}>
        <DialogContent className="max-w-5xl p-0 border-0 bg-transparent shadow-none" data-testid="portfolio-lightbox">
          {active && (
            <div className="relative">
              <img src={active.url} alt={`${active.title} — vetrata panoramica VEPA ${active.category} | VETRO DESIGN ITALIA`} className="w-full max-h-[78vh] object-contain" />

              <button
                onClick={prev}
                data-testid="lightbox-prev"
                aria-label="Foto precedente"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 backdrop-blur-md hover:bg-white transition-colors"
              >
                <ChevronLeft strokeWidth={1.4} className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                data-testid="lightbox-next"
                aria-label="Foto successiva"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 backdrop-blur-md hover:bg-white transition-colors"
              >
                <ChevronRight strokeWidth={1.4} className="w-5 h-5" />
              </button>

              <div className="bg-white p-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-champagne">{active.category}</p>
                  <p className="font-serif text-2xl">{active.title}</p>
                </div>
                <p className="text-xs text-muted-foreground shrink-0" data-testid="lightbox-counter">
                  {activeIdx + 1} / {visible.length}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
